import { VoicemailWithCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/voicemail.pb';
import { HydratedVoicemail, ViewStatus } from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import {
  VoicemailMessageAlertEvent,
  VoicemailTagsAlertEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import dayjs from 'dayjs';
import { omit } from 'lodash-es';
import { useMutation, useQueryClient } from 'react-query';
import { VoicemailApi, VoicemailTypes as VmTypes } from '@frontend/api-voicemails';
import { getWeaveToken } from '@frontend/auth-helpers';
import { getInitialParams } from '@frontend/env';
import { useScopedQuery } from '@frontend/scope';
import { SlidePanelType } from '@frontend/slide-panel';
import { useAlert } from '@frontend/design-system';

export const generateMediaPath = (
  message: VoicemailWithCallRecord | undefined,
  locationId: string,
  currentEnv: string,
  weaveToken: string | undefined
) => {
  const mediaId = message?.forwardedMessageId || message?.mediaId;

  if (!mediaId) {
    return '';
  }
  const mediaPath = `${currentEnv}/phone-exp/voicemail/download/${mediaId}?token=${weaveToken}&location_id=${locationId}`;
  return mediaPath;
};

type VoicemailDetailsType = Awaited<ReturnType<ReturnType<typeof getVoicemailDetailsQueryFn>>>;

export const getVoicemailDetailsQueryFn = ({
  eventParams,
  selectedLocationIds,
  currentEnv,
  weaveToken,
}: {
  eventParams: VoicemailMessageAlertEvent | VoicemailTagsAlertEvent | null;
  selectedLocationIds: string[];
  currentEnv: string;
  weaveToken: string | undefined;
}) => {
  return async () => {
    const { voicemail: { message, person, tagIds } = {} as HydratedVoicemail } = await VoicemailApi.getVoicemail({
      messageId: eventParams?.voicemailMessageId ?? '',
    });

    return {
      firstName: person?.firstName || '',
      lastName: person?.lastName || '',
      contactName: eventParams?.personContact,
      contactId: message?.personId || person?.personId,
      locationIds: eventParams?.locationId ? [eventParams?.locationId] : [],
      channelId: message?.channelId ?? '',
      mailboxId: message?.mailboxId ?? '',
      voiceMailBoxName: eventParams?.mailboxName,
      forwardedMessageId: message?.forwardedMessageId,
      readAt: message?.readAt,
      dateTime: dayjs(message?.createdAt).format('MMM DD YYYY, hh:mm A'),
      mediaFilePath: generateMediaPath(message, selectedLocationIds?.[0], currentEnv, weaveToken),
      mediaId: message?.forwardedMessageId || message?.mediaId,
      contactNumber: message?.callerNumber || eventParams?.callerNumber,
      voicemailId: message?.voicemailId,
      createdAt: message?.createdAt,
      tagIds: tagIds ?? [],
      tags: [],
    };
  };
};

interface UseVmTagHelpersProps {
  oldData: VoicemailDetailsType | undefined;
  tag: Tag;
  voicemailId: string;
}

export const useVoicemailDetailsPanelHelper = ({
  params,
  selectedLocationIds,
  panelType,
  setShow,
}: { params: VoicemailMessageAlertEvent | VoicemailTagsAlertEvent | null } & {
  selectedLocationIds: string[];
  panelType: string;
  setShow: (show: boolean, panelType?: SlidePanelType, context?: unknown) => void;
}) => {
  const queryClient = useQueryClient();
  const alert = useAlert();
  const weaveToken = getWeaveToken();
  const currentEnv = getInitialParams().backendApi;
  const queryKey = ['voicemail-event-panel-details', params?.voicemailMessageId, params?.locationId];

  // fetch voicemail details
  const voicemailDetailsQuery = useScopedQuery({
    queryKey: queryKey,
    queryFn: getVoicemailDetailsQueryFn({
      eventParams: params,
      selectedLocationIds,
      currentEnv,
      weaveToken,
    }),
    onError: () => {
      alert.error('Error fetching voicemail data');
    },
    enabled: !!params?.voicemailMessageId,
  });

  const optimisticApplyTag = ({ oldData, tag, voicemailId }: UseVmTagHelpersProps) => {
    if (oldData) {
      if (oldData.voicemailId === voicemailId) {
        return {
          ...oldData,
          tagIds: oldData.tagIds ? Array.from(new Set([...oldData.tagIds, tag.id])) : [tag.id],
        };
      }
    }
    return undefined;
  };

  const optimisticRemoveTag = ({ oldData, tag, voicemailId }: UseVmTagHelpersProps) => {
    if (oldData) {
      if (oldData.voicemailId === voicemailId) {
        return {
          ...oldData,
          tagIds: oldData.tagIds ? oldData.tagIds.filter((tg) => tg !== tag.id) : [],
        };
      }
    }
    return undefined;
  };

  const { mutateAsync: updateReadStatusAsync } = useMutation(
    (payload: VmTypes.StatusPayload) => {
      const req = omit(payload, 'index');
      return VoicemailApi.setVoicemailViewStatus(req);
    },
    {
      onSuccess: (response, { status }) => {
        if (!queryKey) return;

        const data = queryClient.getQueryData<VoicemailDetailsType>([selectedLocationIds, ...queryKey]);

        if (data) {
          const updatedData = {
            ...data,
            readAt:
              status === ViewStatus.VIEW_STATUS_READ && response.voicemail?.readEpoch
                ? // @ts-ignore - This type is wrong, the schema says it should be a number but it isn't
                  new Date(parseInt(response.voicemail.readEpoch * 1000)).toISOString()
                : undefined,
          };
          queryClient.setQueryData([selectedLocationIds, ...queryKey], updatedData);
        }
      },
    }
  );

  const { mutate: onDelete } = useMutation((req: VmTypes.DeletePayload) => VoicemailApi.deleteVoicemailMedia(req), {
    onSuccess: () => {
      if (panelType === 'voicemailTranscription') {
        setShow(false);
      }
    },
  });

  return {
    voicemailDetailsQuery,
    optimisticApplyTag,
    optimisticRemoveTag,
    updateReadStatusAsync,
    onDelete,
  };
};
